// LocalStorage-based user preferences for metric unit display
import { UnitType } from './unitConversions';

// Locales that use imperial units (only US English)
const IMPERIAL_LOCALES = ['en'];

// Default unit preferences for metric system
const METRIC_DEFAULTS: Record<string, UnitType> = {
  'water_gallons': 'L',
  'miles_ran': 'km',
  'body_weight': 'kg',
};

// Default unit preferences for imperial system
const IMPERIAL_DEFAULTS: Record<string, UnitType> = {
  'water_gallons': 'gal',
  'miles_ran': 'mi',
  'body_weight': 'lbs',
};

/**
 * Determine which unit system a locale uses.
 * Only US English ('en') uses imperial; all others (including 'en-GB') use metric.
 */
export const getUnitSystemForLocale = (locale: string): 'metric' | 'imperial' => {
  return IMPERIAL_LOCALES.includes(locale) ? 'imperial' : 'metric';
};

/**
 * Apply locale-appropriate unit defaults for all convertible metrics.
 * Called when the user changes language or on first launch after language detection.
 */
export const applyLocaleUnitDefaults = (locale: string): void => {
  const system = getUnitSystemForLocale(locale);
  const defaults = system === 'metric' ? METRIC_DEFAULTS : IMPERIAL_DEFAULTS;

  for (const [metricKey, unit] of Object.entries(defaults)) {
    setUnitPreference(metricKey, unit);
  }

  console.log(`[UnitPreferences] Applied ${system} unit defaults for locale: ${locale}`);
};

const STORAGE_KEY = 'gitfit_unit_preferences';

export interface UnitPreferences {
  [metricKey: string]: UnitType;
}

// Get all unit preferences from localStorage
export const getUnitPreferences = (): UnitPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading unit preferences:', error);
    return {};
  }
};

// Get unit preference for a specific metric
export const getUnitPreference = (metricKey: string): UnitType | null => {
  const prefs = getUnitPreferences();
  return prefs[metricKey] || null;
};

// Set unit preference for a specific metric
export const setUnitPreference = (metricKey: string, unit: UnitType): void => {
  try {
    const prefs = getUnitPreferences();
    prefs[metricKey] = unit;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (error) {
    console.error('Error saving unit preference:', error);
  }
};

// Clear all unit preferences
export const clearUnitPreferences = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing unit preferences:', error);
  }
};
